import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { InsertarCargaRequest } from '@/app/models/Carga';
import {
  aEnteroNoNegativo,
  aTextoAcotado,
  cumplePatron,
  esImagenDataUrlValida,
  PATRONES_VALIDACION
} from '@/app/services/security/input-validation';
import { UrlValidationService } from '@/app/services/security/url-validation.service';

/** Longitudes máximas de los campos de texto que se transmiten. */
const LIMITES_TEXTO = {
  idCombustible: 50,
  almacen: 100,
  cantidad: 20,
  fecha: 40,
  observacion: 500,
  iata: 10
} as const;

@Injectable({
  providedIn: 'root'
})
export class CargaService {

  private urlValidation = inject(UrlValidationService);

  private readonly endpoint: string;

  constructor
  (
    private httpClient: HttpClient
  ) 
  { 
    this.endpoint = this.urlValidation.construirEndpointApi('/cargas');
  }

  getLoads(station: string){
    return this.httpClient.get(this.endpoint + '/ObtenerCargas/'+ station );
  }

  searchLoads(parametros: any){
    return this.httpClient.post(this.endpoint + '/BuscarCargas/', parametros);
  }

  getLoadDetail(idCarga: number){
    return this.httpClient.get(this.endpoint +  '/ObtenerCarga/' + idCarga);
  }

  sendingToMaximo(carga: any){
    return this.httpClient.post(this.endpoint + '/EnviarCargaMaximo',carga);
  }

  updateLoad(updateLoad: any){
    return this.httpClient.post(this.endpoint + '/ActualizarCarga',updateLoad);
  }

  cancelLoad(idCarga: any,idUsuario: any){
    return this.httpClient.get(this.endpoint + '/CancelarCarga/'+idCarga+'/'+idUsuario);
  }

  /**
   * Registra una carga. El cuerpo de la petición se construye campo por campo a
   * partir del contrato InsertarCargaRequest: no se envía el objeto del
   * formulario tal cual, de modo que sólo salen del navegador los datos
   * estrictamente necesarios para la operación.
   */
  insertLoad(cargaRq: any): Observable<any>{
    let cuerpo: InsertarCargaRequest;
    try {
      cuerpo = this.construirCargaParaEnvio(cargaRq);
    } catch (error) {
      return throwError(() => error);
    }
    return this.httpClient.post(this.endpoint + '/InsertarCarga', cuerpo);
  }

  autorizacionCarga(load: any){
    return this.httpClient.put(this.endpoint + `/AutorizacionCarga/${load.idCarga}/${load.idUsuario}`,{});
  }

  /**
   * Copia únicamente los campos del contrato, normalizando tipos y longitudes.
   * Los valores numéricos se envían como enteros, los textos recortados y la
   * evidencia fotográfica sólo si es una imagen válida; así se evita transmitir
   * campos auxiliares del formulario, contenido de archivos de otro tipo o
   * datos de la sesión que la operación no requiere.
   *
   * @throws Error si los datos obligatorios no cumplen el formato esperado.
   */
  private construirCargaParaEnvio(load: any): InsertarCargaRequest {
    const origen = load ?? {};

    const fechaCarga = aTextoAcotado(origen.fechaCarga, LIMITES_TEXTO.fecha);
    if (!cumplePatron(fechaCarga, PATRONES_VALIDACION.fechaIso)) {
      throw new Error('La fecha de carga no cumple el formato permitido.');
    }

    // No se recorta: si el valor no es un código IATA se rechaza en vez de mutarlo.
    const iata = aTextoAcotado(origen.IATA, LIMITES_TEXTO.iata);
    if (!cumplePatron(iata, PATRONES_VALIDACION.codigoIata)) {
      throw new Error('La estación seleccionada no tiene un código IATA válido.');
    }

    return {
      idOperador: aEnteroNoNegativo(origen.idOperador),
      idDespachador: aEnteroNoNegativo(origen.idDespachador),
      idEquipo: aEnteroNoNegativo(origen.idEquipo),
      idCombustible: this.aClaveCombustible(origen.idCombustible),
      almacen: aTextoAcotado(origen.almacen, LIMITES_TEXTO.almacen),
      // Se repiten en el cliente las validaciones del formulario antes de enviar.
      litrosCargados: this.aCantidad(origen.litrosCargados, 'litros cargados'),
      horometro: this.aCantidad(origen.horometro, 'horómetro'),
      pathFoto: this.aEvidenciaFotografica(origen.pathFoto),
      fechaCarga,
      idEstacion: aEnteroNoNegativo(origen.idEstacion),
      creadoPor: aEnteroNoNegativo(origen.creadoPor),
      observacion: aTextoAcotado(origen.observacion, LIMITES_TEXTO.observacion),
      idAlmacen: aEnteroNoNegativo(origen.idAlmacen),
      IATA: iata.toUpperCase()
    };
  }

  /**
   * Valida una cantidad decimal positiva conservando el tipo original: los
   * campos numéricos del formulario se envían como número y no como texto, para
   * no alterar el contrato con la API.
   *
   * @throws Error si el valor no cumple el formato permitido.
   */
  private aCantidad(valor: unknown, campo: string): string | number {
    const texto = aTextoAcotado(String(valor ?? ''), LIMITES_TEXTO.cantidad);
    if (!cumplePatron(texto, PATRONES_VALIDACION.decimalPositivo)) {
      throw new Error(`El valor de ${campo} no cumple el formato permitido.`);
    }
    return typeof valor === 'number' ? valor : texto;
  }

  /**
   * Acepta la evidencia sólo si es un data URL de imagen png o jpeg. Si trae
   * contenido de otro tipo se rechaza el envío en lugar de transmitirlo, para no
   * subir al backend el contenido de un archivo que no sea la foto esperada.
   *
   * @throws Error si el valor no es una imagen válida.
   */
  private aEvidenciaFotografica(valor: unknown): string {
    if (valor === null || valor === undefined || valor === '') {
      return '';
    }

    const dataUrl = String(valor).replace(/\s+/g, '');
    if (!esImagenDataUrlValida(dataUrl)) {
      throw new Error('La evidencia fotográfica debe ser una imagen png o jpeg.');
    }

    return dataUrl;
  }

  /** Normaliza la clave de combustible conservando su tipo (texto o número). */
  private aClaveCombustible(valor: unknown): string | number {
    if (typeof valor === 'number') {
      return aEnteroNoNegativo(valor);
    }
    const texto = aTextoAcotado(valor, LIMITES_TEXTO.idCombustible);
    return cumplePatron(texto, PATRONES_VALIDACION.claveCatalogo) ? texto : '';
  }
}
